import axios from 'axios';
import { apiConfig } from '../authConfig';

// Create axios instance with base configuration
const apiClient = axios.create({
    baseURL: apiConfig.baseUrl,
    timeout: 0, // No timeout limit - let requests run as long as needed
    headers: {
        'Content-Type': 'application/json',
    },
});

// Store auth context reference (will be set by AuthProvider)
let authContextRef = null;

export const setAuthContext = (authContext) => {
    authContextRef = authContext;
};

// Request interceptor - ADD AUTHENTICATION HEADER
apiClient.interceptors.request.use(
    async (config) => {
        // Add authentication token if available
        if (authContextRef && authContextRef.token) {
            config.headers.Authorization = `Bearer ${authContextRef.token}`;
            console.log('🔐 API Client: Added Authorization header');
        } else {
            console.warn('⚠️ API Client: No token available for request');
        }

        // Handle file uploads
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle authentication errors
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Handle 401 Unauthorized errors - DON'T REDIRECT TO LOGIN
        if (error.response?.status === 401) {
            console.error('❌ API Client: 401 Unauthorized - Token invalid or expired');
            // DON'T automatically redirect to login - let the component handle it
            // Just log the error and return it to the calling component
        }

        // Handle 403 Forbidden errors
        if (error.response?.status === 403) {
            console.error('Access forbidden - insufficient permissions');
        }

        // Handle network errors
        if (!error.response) {
            console.error('Network error - API may be unavailable');
        }

        return Promise.reject(error);
    }
);

// API methods
export const api = {
    // Authentication
    getAuthConfig: () => apiClient.get(apiConfig.endpoints.authConfig),
    getCurrentUser: () => apiClient.get(apiConfig.endpoints.authUser),

    // Health check
    healthCheck: () => apiClient.get(apiConfig.endpoints.health),

    // File operations
    uploadFiles: (formData) => apiClient.post(apiConfig.endpoints.upload, formData),
    getCandidateDetails: (candidateId) => apiClient.get(`${apiConfig.endpoints.candidate}/${candidateId}`),
    downloadFile: (filename) => apiClient.get(`${apiConfig.endpoints.download}/${filename}`, {
        responseType: 'blob'
    }),
    downloadAll: () => apiClient.get(apiConfig.endpoints.downloadAll, {
        responseType: 'blob'
    }),

    // Analytics
    getStats: () => apiClient.get(apiConfig.endpoints.stats),
};

export default apiClient;
